import { useQuery } from "@tanstack/react-query";
import {
  type CalculateSavedPaymentAccountTransactionsResponse,
  type CashierTradingAccount,
  calculateSavedPaymentAccountTransactions,
  type SavedPaymentAccountProfitAmount,
  type SavedPaymentAccountRefundAmounts,
} from "@/api/cashier";

// How long a seeded calculation (carried over via navigation state from the
// choose-method step) is treated as fresh before Summary re-fetches it -
// mirrors the page's own auto-refresh cadence (see TRANSACTIONS_REFETCH_MS).
const SEEDED_DATA_STALE_MS = 20_000;

export const useSummaryData = ({
  selectedAccount,
  refundAmounts,
  profitAmount,
  enabled = true,
  initialData,
  initialDataUpdatedAt,
}: {
  selectedAccount: CashierTradingAccount | undefined;
  refundAmounts: SavedPaymentAccountRefundAmounts[];
  profitAmount: SavedPaymentAccountProfitAmount | undefined;
  enabled?: boolean;
  initialData?: CalculateSavedPaymentAccountTransactionsResponse;
  initialDataUpdatedAt?: number;
}) => {
  return useQuery({
    queryKey: [
      "calculateSavedPaymentAccountTransactions",
      selectedAccount?.id,
      refundAmounts,
      profitAmount,
    ],
    queryFn: async () => {
      if (!selectedAccount?.id) return;

      const { data } = await calculateSavedPaymentAccountTransactions({
        path: {
          tradingAccountID: selectedAccount.id,
        },
        body: {
          refund_saved_payment_accounts: refundAmounts,
          profit_saved_payment_account: profitAmount,
        },
        throwOnError: true,
      });
      return data;
    },
    enabled: enabled && !!selectedAccount?.id && !!refundAmounts,
    initialData,
    initialDataUpdatedAt,
    staleTime: initialData !== undefined ? SEEDED_DATA_STALE_MS : undefined,
  });
};
